import React, {
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { cn } from 'cn';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import Info from '../info';
import Spinner from '../spinner';
import { SearchQueryContext } from '../filtro/SearchQueryContext';
import {
  findLabelsInChildren,
  filterChildren,
  extractHeaderText,
  normalizeText,
} from '../filtro/searchUtils';
import { capitalizeFirstLetter } from '../../utils/string';

const IGNORAR_CLIQUE_TITULO =
  'button, a, input, select, textarea, [role="button"], [data-slot="tooltip-trigger"]';

type PanelCollapseProps = {
  /** Título do card; clicar nele abre e fecha. */
  header?: React.ReactNode;
  /** Texto menor abaixo do título. */
  subtitle?: React.ReactNode;
  /** Conteúdo do card. */
  children?: React.ReactNode;
  /** Aberto ou fechado (padrão: aberto). Ao mudar, abre ou fecha. */
  open?: boolean;
  /** Com `false`, fica sempre aberto e sem a seta. */
  collapsible?: boolean;
  /** Só monta o conteúdo quando abre (conteúdo pesado ou com `lazy`). Depois de aberto pelo clique, continua montado ao fechar. */
  loadChildrenOnOpen?: boolean;
  /** Chamado antes de abrir ou fechar pelo clique. */
  onCollapse?: () => void;
  /** Chamado depois de abrir ou fechar pelo clique, com o novo estado. */
  collapseCallback?: (open: boolean) => void;
  /** Ações ao lado do título. Clique em botão, link, campo ou `role="button"` não abre nem fecha; num ícone solto, abre e fecha. */
  headerActions?: React.ReactNode;
  /** Texto do ícone "i" de ajuda ao lado do título. */
  textInfoTitle?: React.ReactNode;
  /** Borda vermelha (erro de validação no conteúdo). */
  errors?: unknown;
  /** Sem sombra. */
  hideShadow?: boolean;
  /** Padding menor (16px em volta). */
  noPadding?: boolean;
  /** Visual de seção dentro de drawer: sem card, título em negrito na cor primária. */
  ehAccordionDrawer?: boolean;
  /** Mostra só a seta, sem título. */
  collapseWithoutHeader?: boolean;
  /** Distância entre o título e o conteúdo aberto, em px. */
  contentMarginTop?: number;
  /** Classes do título. */
  headerStyle?: string;
  /** Estilo do título. */
  styleTitle?: React.CSSProperties;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Card com título que abre e fecha o conteúdo. Dentro do drawer de filtro com busca, abre sozinho quando o conteúdo bate com a busca.
 * @import import PanelCollapse from 'src/componentes/panel-collapse';
 */
function PanelCollapse({
  open: openProp = true,
  header,
  children,
  subtitle,
  headerStyle,
  errors = null,
  hideShadow = false,
  loadChildrenOnOpen = false,
  style,
  styleTitle,
  collapsible = true,
  collapseCallback,
  onCollapse,
  noPadding = false,
  ehAccordionDrawer = false,
  headerActions = [],
  collapseWithoutHeader = false,
  className,
  textInfoTitle,
  contentMarginTop = 28,
}: PanelCollapseProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(openProp);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const [animando, setAnimando] = useState(false);
  const previousOpenProp = useRef(openProp);
  const previousSearchQuery = useRef<string | null>(null);
  const pendingCollapseCallback = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const searchContext = useContext(SearchQueryContext);
  const searchQuery = searchContext?.searchQuery || null;
  const searchIntl = searchContext?.intl || null;
  const hasSearchQuery = Boolean(searchQuery && searchQuery.trim());
  const hasMatch =
    hasSearchQuery && findLabelsInChildren(children, searchQuery, searchIntl);
  const headerMatches =
    hasSearchQuery &&
    Boolean(header) &&
    normalizeText(extractHeaderText(header)).includes(
      normalizeText(searchQuery)
    );

  useLayoutEffect(() => {
    if (openProp !== previousOpenProp.current) {
      previousOpenProp.current = openProp;
      setOpen(openProp);
    }
  }, [openProp]);

  useEffect(() => {
    if (searchQuery === previousSearchQuery.current) {
      return;
    }

    previousSearchQuery.current = searchQuery;
    if (hasMatch || headerMatches) {
      setOpen(true);
    }
  }, [searchQuery, hasMatch, headerMatches]);

  useLayoutEffect(() => {
    if (pendingCollapseCallback.current) {
      pendingCollapseCallback.current = false;
      collapseCallback?.(open);
    }
  }, [open, childrenLoaded, collapseCallback]);

  const handleCollapse = () => {
    if (!collapsible) {
      return;
    }

    onCollapse?.();
    pendingCollapseCallback.current = true;
    setOpen((previousOpen) => !previousOpen);
    setChildrenLoaded(true);
  };

  const isOpen = collapsible ? open : true;

  // animando liga no mesmo render do open: no animationstart, a página piscava.
  const [conteudoVisivel, setConteudoVisivel] = useState(isOpen);
  const [openAnterior, setOpenAnterior] = useState(isOpen);
  if (isOpen !== openAnterior) {
    setOpenAnterior(isOpen);
    setAnimando(true);
    if (isOpen) setConteudoVisivel(true);
  }

  const terminarAnimacao = () => {
    setAnimando(false);
    if (!isOpen) setConteudoVisivel(false);
  };

  // Escondido ou sem animação, o animationend não dispara.
  useLayoutEffect(() => {
    if (!animando) return;
    const node = contentRef.current;
    if (
      !node ||
      !node.getClientRects().length ||
      getComputedStyle(node).animationName === 'none'
    ) {
      terminarAnimacao();
    }
  });

  useEffect(() => {
    if (!animando) return undefined;
    const timer = setTimeout(terminarAnimacao, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animando, isOpen]);

  if (hasSearchQuery && !hasMatch && !headerMatches) {
    return null;
  }

  const visibleChildren = hasMatch
    ? filterChildren(children, searchQuery, searchIntl) || children
    : children;

  const Chevron = isOpen ? ChevronUp : ChevronDown;

  return (
    <Collapsible asChild open={isOpen} onOpenChange={handleCollapse}>
      <Card
        style={{
          marginBottom: ehAccordionDrawer ? 0 : 15,
          marginTop: ehAccordionDrawer ? 20 : undefined,
          ...style,
        }}
        className={cn(
          'card card-body',
          ehAccordionDrawer
            ? 'tw:border-0 tw:bg-transparent tw:p-0 tw:shadow-none'
            : noPadding
            ? 'tw:p-4'
            : 'tw:px-6 tw:py-4',
          hideShadow && 'tw:shadow-none',
          !!errors && 'tw:border-destructive',
          className
        )}
      >
        {(header || (collapsible && collapseWithoutHeader)) && (
          <>
            <CardTitle
              onClick={
                collapsible
                  ? (e) => {
                      const target = e.target as Element;
                      if (target.closest(IGNORAR_CLIQUE_TITULO)) return;
                      handleCollapse();
                    }
                  : undefined
              }
              className={cn(
                'card-title',
                collapsible && 'tw:cursor-pointer',
                ehAccordionDrawer && 'tw:pb-1.5 tw:font-bold tw:text-primary',
                headerStyle
              )}
              style={styleTitle}
            >
              {header}
              {textInfoTitle && (
                <Info
                  className="tw:ml-1"
                  id={`info-title-panel-${header}`}
                  placement="right"
                  text={textInfoTitle}
                />
              )}
              {headerActions}
              {collapsible && (
                <CollapsibleTrigger
                  aria-label={capitalizeFirstLetter(
                    intl.formatMessage({ id: isOpen ? 'recolher' : 'expandir' })
                  )}
                  className="tw:float-right tw:ml-auto tw:inline-flex tw:shrink-0 tw:cursor-pointer tw:rounded-sm tw:leading-0 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring/50"
                >
                  <Chevron
                    size={ehAccordionDrawer ? 20 : 16}
                    strokeWidth={1.75}
                  />
                </CollapsibleTrigger>
              )}
            </CardTitle>
            {subtitle && (
              <CardDescription className="tw:mt-1 tw:text-sm tw:leading-normal">
                {subtitle}
              </CardDescription>
            )}
          </>
        )}
        <CollapsibleContent
          ref={contentRef}
          forceMount
          hidden={!conteudoVisivel}
          onAnimationEnd={(e) => {
            if (e.target !== e.currentTarget) return;
            // Direto no DOM: o re-render só sai no quadro seguinte.
            if (!isOpen) e.currentTarget.hidden = true;
            terminarAnimacao();
          }}
          // Fixo, cortaria menus sem portal (react-select).
          className={cn(animando && 'tw:overflow-hidden')}
          style={{
            marginTop: !ehAccordionDrawer && isOpen ? contentMarginTop : 0,
            transition: 'margin-top 0.3s ease-out',
          }}
        >
          <Suspense
            fallback={
              <div className="tw:flex tw:justify-center tw:py-4">
                <Spinner className="tw:size-8" />
              </div>
            }
          >
            {!loadChildrenOnOpen || isOpen || childrenLoaded
              ? visibleChildren
              : null}
          </Suspense>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default PanelCollapse;
